import React, { ChangeEvent, useEffect, useState } from "react";
import CustomInput from "../components/CustomInput";
import CustomRadioButton from "../components/CustomRadioButton";
import CustomSelect from "../components/CustomSelect";
import ModalPortal from "../components/modal-portal";
import FormSection, { FieldLabel, dashedButtonClass, primaryButtonClass, secondaryButtonClass } from "../components/form-section";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleNotch, faPlus, faXmark } from "@fortawesome/free-solid-svg-icons";
import GenerateRandomId from "../utils/generateRandomId";
import { InsuranceProviderInterface } from "../types/provider-types";
import { ParseErrors } from "../utils/ParseErrors";
import axiosInstance from "../utils/axiosInstance";
import { providerPermissionFields } from "../components/providerComponents/providersData";
import { toast } from "react-toastify";
import { useDebounceCallback, useDebounceValue } from "usehooks-ts";
import { useChaseEditModel } from "../store/use-chase-edit-model";
import EditDriverForm from "../components/createChaseComponent/EditDriverForm";
import Chip from "../components/Chip";
import { chaseStatusData } from "../components/chaseListComponents/chaseListData";
import GenderData from "../utils/data/genderData";

interface EXInsuranceProviderInterface extends InsuranceProviderInterface {
    label: string
}


interface IEditChaseModel {
    UpdateItemData: () => void
}

const EditChaseModel: React.FC<IEditChaseModel> = ({ UpdateItemData }) => {
    const { initialValues, onClose } = useChaseEditModel()
    const { chaseItem: chaseItems, drivers: chaseDrivers } = initialValues?.data
    const [values, setValues] = useState<Record<string, any>>({
        chaseItems: chaseItems?.map((chaseItem) => ({ ...providerPermissionFields?.find((provData) => provData.id === chaseItem?.type), ...chaseItem })) || [],
        drivers: chaseDrivers
    })
    const [providers, setProviders] = useState<EXInsuranceProviderInterface[]>([])
    const [loading, setLoading] = useState<boolean>(false)
    const [providerSearch, setProviderSearch] = useDebounceValue("", 300)
    const [pLoading, setPLoading] = useState<boolean>(false)

    // --------------------------------- [ HANDLE SUBMIT ]-------------------------------------

    const updateChase = async () => {
        if (ValidateDrivers()) {
            setLoading(true)
            try {
                const res = await axiosInstance.patch(`/chase/${initialValues?.id}`, { ...values, drivers: values.drivers.map((item) => ({ ...item, chaseType: GetDefaultValues("chaseType") })) })
                UpdateItemData()
                toast.success(res.data?.message)
                onClose()
                setLoading(false)
            } catch (error) {
                toast.error(ParseErrors(error))
                setLoading(false)
                console.log(error)
            }
        }
    }

    // ---------------------------------- [ UPDATE INPUT VALUES ] --------------------------------

    const HandleChangeValues = useDebounceCallback((e: ChangeEvent<HTMLInputElement>) => {
        setValues((prev) => ({ ...prev, [e.target.name]: e.target.value }))
    }, 200)


    const SelectProvider = (option: EXInsuranceProviderInterface) => {
        setValues((prev) => ({ ...prev, provider: option?.label, insurerId: option?.id }))
    }

    const HandleAddChaseItem = (chaseItem: Record<string, any>) => {
        const isChase = values?.chaseItems.find((chase) => (chase?.type || chase?.id) === chaseItem?.id)

        if (isChase?.delete) {
            setValues((prev) => ({ ...prev, chaseItems: [...prev.chaseItems.map((chaseItm) => chaseItm.id === isChase?.id ? { ...chaseItm, delete: false } : chaseItm)] }))
        } else if (!isChase) {
            setValues((prev) => ({ ...prev, chaseItems: [...prev.chaseItems, chaseItem] }))
        }
    }


    const HandleDeleteChaseItem = (chaseItem: Record<string, any>) => {
        const isDefaultValue = chaseItems?.find((chase) => chaseItem?.type ? chase?.type === chaseItem?.type : chase?.type === chaseItem?.id)

        if (isDefaultValue) {
            setValues((prev) => ({ ...prev, chaseItems: [...prev.chaseItems.map((item) => item?.id === chaseItem.id ? { ...item, delete: true } : item)] }))
        } else {
            setValues((prev) => ({ ...prev, chaseItems: [...prev.chaseItems.filter((item) => item?.id !== chaseItem.id)] }))
        }
    }

    // --------------------------------------- [ DRIVERS FUNCTIONS ] -----------------------------------

    const AddNewDriver = () => {
        setValues((prev) => ({ ...prev, drivers: [...prev.drivers, { id: GenerateRandomId() }] }))
    }

    const RemoveDriver = (rId: string | number) => {
        const defaultDriver = chaseDrivers.find((driverData) => driverData.id === rId)

        if (defaultDriver) {
            setValues((prev) => ({ ...prev, drivers: [...prev?.drivers?.map((driver) => driver.id === defaultDriver.id ? { ...defaultDriver, delete: true } : driver)] }))
        } else {
            setValues((prev) => ({ ...prev, drivers: values.drivers.filter((driver) => driver.id !== rId) }))
        }
    }

    const fetchDrivers = (driversData: Record<string, any>) => {
        const isDriver = values?.drivers?.find((driver) => driver.id === driversData?.id)

        if (!isDriver) {
            setValues((prev) => ({ ...prev, drivers: [...prev?.drivers, driversData] }))
        } else {
            const drivers = values?.drivers.map((item) => item?.id === driversData?.id ? driversData : item)
            setValues((prev) => ({ ...prev, drivers: drivers }))
        }
    }


    // -------------------------------- [ FIND PROVIDERS ] --------------------------------------

    useEffect(() => {
        const findProviders = async () => {
            setPLoading(true)
            try {
                const providerRes = await axiosInstance.get(providerSearch ? `/provider?search=${providerSearch}` : "/provider")
                setProviders(providerRes.data.data?.map((providerItem) => ({ ...providerItem, label: `${providerItem.name} (${providerItem.type} - ${providerItem?.productType}/${providerItem?.schemeCode})` })))
                setPLoading(false)
            } catch (error) {
                ParseErrors(error)
                setPLoading(false)
            }
        }

        findProviders()
    }, [providerSearch])



    const GetDefaultValues = (name: string) => {
        return values[name] || initialValues?.data[name]
    }


    const ValidateDrivers = () => {
        const newDriver = values.drivers.find((newDriverData) => isNaN(newDriverData?.id) && (!newDriverData.name || !newDriverData.chaseType))

        if (newDriver) {
            toast.error("Please fill all fields of Additional Driver")
            return false
        } else {
            return true
        }
    }

    return (
        <ModalPortal>
            <div className="z-[99999] fixed inset-0 bg-[color:var(--shadow-modal-scrim)] backdrop-blur-sm flex items-start justify-center overflow-y-auto py-6 sm:py-10 px-3 sm:px-5 animate-fade-in" onClick={onClose}>
                <div className="bg-surface border border-edge w-full max-w-[880px] smallTablet:max-w-[calc(100vw-2rem)] rounded-2xl shadow-overlay" onClick={(e) => e.stopPropagation()}>
                    {/*---------------------------- [ HEADER ] ------------------------------- */}
                    <div className="flex items-start justify-between gap-4 px-6 sm:px-8 py-5 smallTablet:px-4 smallTablet:py-4 border-b border-edge-faint rounded-t-2xl">
                        <div className="min-w-0">
                            <h2 className="text-[22px] mobile:text-[18px] font-bold text-text-primary leading-tight">Update chase</h2>
                            <p className="mt-1 text-[13px] font-medium text-light-gray truncate">
                                {GetDefaultValues("firstName")} {GetDefaultValues("surname")} · {GetDefaultValues("policyReferenceNumber")}
                            </p>
                        </div>
                        <button
                            onClick={onClose}
                            className="shrink-0 h-9 w-9 flex items-center justify-center rounded-lg border border-edge text-light-gray hover:text-text-primary hover:border-indigo-400/40 transition-all duration-200"
                            aria-label="Close"
                        >
                            <FontAwesomeIcon icon={faXmark} className="text-[14px]" />
                        </button>
                    </div>
                    {/*---------------------------- [ HEADER ] ------------------------------- */}

                    {/* -------------------------- [ BODY ] ------------------------------------ */}
                    <FormSection title="Customer">
                        <CustomInput label="First name" defaultValue={GetDefaultValues("firstName")} name="firstName" onChange={HandleChangeValues} />
                        <CustomInput label="Surname" defaultValue={GetDefaultValues("surname")} name="surname" onChange={HandleChangeValues} />
                        <CustomInput label="Email address" defaultValue={GetDefaultValues("email")} name="email" onChange={HandleChangeValues} />
                        <CustomInput label="Phone number" defaultValue={GetDefaultValues("phone")} name="phone" onChange={HandleChangeValues} />
                        <CustomInput type="date" label="Date of birth" autoComplete="none" defaultValue={GetDefaultValues("dob")} name="dob" onChange={HandleChangeValues} />
                        <CustomSelect
                            comboboxType="select"
                            options={GenderData}
                            labelProperty="label"
                            valueProperty="id"
                            label="Gender"
                            placeholder="Select gender"
                            onSingleSelectChange={(option) => setValues((prev) => ({ ...prev, gender: option.id }))}
                            value={GenderData.find((gender) => gender.id === GetDefaultValues("gender"))}
                        />
                    </FormSection>

                    <FormSection title="Policy">
                        <CustomInput label="Policy reference number" defaultValue={GetDefaultValues("policyReferenceNumber")} name="policyReferenceNumber" onChange={HandleChangeValues} />
                        <CustomInput type="text" label="Registration no" autoComplete="none" defaultValue={GetDefaultValues("regNo")} name="regNo" onChange={HandleChangeValues} />
                        <CustomSelect
                            comboboxType='select' label="Chase status" labelProperty="name" showTop
                            valueProperty="name" options={chaseStatusData}
                            onSingleSelectChange={(option) => setValues((prev) => ({ ...prev, status: option?.name }))}
                            value={chaseStatusData.find((type) => type.name === GetDefaultValues("status"))}
                        />
                        <CustomInput label="Agent code" defaultValue={GetDefaultValues("customerExCode")} name="customerExCode" onChange={HandleChangeValues} />

                        <div className="sm:col-span-2">
                            <CustomSelect
                                comboboxType="select"
                                options={providers}
                                labelProperty="label"
                                valueProperty="id"
                                label="Provider"
                                placeholder="Select provider"
                                onSingleSelectChange={(option: EXInsuranceProviderInterface) => SelectProvider(option)}
                                value={providers.find((provData) => provData.id === GetDefaultValues("insurerId"))}
                                onInputChange={(value) => setProviderSearch(value)}
                                isLoading={pLoading}
                            />
                        </div>
                    </FormSection>

                    <FormSection
                        title="Chase items"
                        description="Pick an item to add it. Click a chip to remove it."
                        className="!grid-cols-1"
                    >
                        <CustomSelect
                            comboboxType="select"
                            options={providerPermissionFields}
                            labelProperty="label"
                            valueProperty="id"
                            label="Add chase item"
                            placeholder="Select items"
                            onSingleSelectChange={(option: Record<string, any>) => HandleAddChaseItem(option)}
                        />

                        {values?.chaseItems.filter((chaseItem) => !chaseItem?.delete)?.length ? (
                            <div className="flex items-center justify-start flex-wrap gap-2">
                                {values?.chaseItems.filter((chaseItem) => !chaseItem?.delete)?.map((chaseItem, index) => {
                                    return <Chip result={chaseItem?.label} key={index} className="cursor-pointer" onClick={() => HandleDeleteChaseItem(chaseItem)} />
                                })}
                            </div>
                        ) : (
                            <p className="text-[13px] font-medium text-light-gray">No chase items selected yet.</p>
                        )}
                    </FormSection>

                    <FormSection title="Preferences">
                        <div>
                            <FieldLabel>Policy type</FieldLabel>
                            <div className="mt-2 flex flex-wrap gap-2">
                                <CustomRadioButton
                                    id="standard"
                                    name="policyType"
                                    label="Standard"
                                    checked={GetDefaultValues("policyType") === "Standard"}
                                    onChange={HandleChangeValues}
                                    value="Standard"
                                />
                                <CustomRadioButton
                                    id="Nonstandard"
                                    name="policyType"
                                    label="Non standard"
                                    value="Non-Standard"
                                    checked={GetDefaultValues("policyType") === "Non-Standard"}
                                    onChange={HandleChangeValues}
                                />
                            </div>
                        </div>

                        <div>
                            <FieldLabel>Department</FieldLabel>
                            <div className="mt-2 flex flex-wrap gap-2">
                                <CustomRadioButton
                                    id="sales"
                                    name="chaseType"
                                    label="Sales"
                                    checked={GetDefaultValues("chaseType") === "Sales"}
                                    onChange={HandleChangeValues}
                                    value="Sales"
                                />
                                <CustomRadioButton
                                    id="Renewals"
                                    name="chaseType"
                                    label="Renewals"
                                    value="Renewals"
                                    checked={GetDefaultValues("chaseType") === "Renewals"}
                                    onChange={HandleChangeValues}
                                />
                            </div>
                        </div>

                        <div className="sm:col-span-2">
                            <FieldLabel>Driver</FieldLabel>
                            <div className="mt-2 flex flex-wrap gap-2">
                                <CustomRadioButton
                                    id="mainDriver"
                                    label="Main driver"
                                    name="driver"
                                    checked={GetDefaultValues("driver") === "Main Driver"}
                                    onChange={HandleChangeValues}
                                    value="Main Driver"
                                />
                                <CustomRadioButton
                                    id="additionalDriver"
                                    label="Additional driver"
                                    name="driver"
                                    value="Additional Driver"
                                    checked={GetDefaultValues("driver") === "Additional Driver"}
                                    onChange={HandleChangeValues}
                                />
                            </div>
                        </div>
                    </FormSection>

                    {/* ------------------------- [ DRIVERS ] -------------------------------- */}
                    <FormSection
                        title="Additional drivers"
                        description="Chase documents from anyone else named on the policy."
                        className="!grid-cols-1"
                    >
                        {
                            values?.drivers.filter((driverItem) => !driverItem?.delete).map((item) => {
                                let keyId = item?.id
                                return (
                                    <EditDriverForm key={keyId} item={{ ...item, chaseType: GetDefaultValues("chaseType") }} RemoveDriver={RemoveDriver} fetchDrivers={fetchDrivers} />
                                )
                            })
                        }

                        <button
                            type="button"
                            onClick={AddNewDriver}
                            disabled={loading}
                            className={dashedButtonClass}
                        >
                            <FontAwesomeIcon icon={faPlus} className="text-[12px]" />
                            Add additional driver
                        </button>
                    </FormSection>
                    {/* ------------------------- [ DRIVERS ] -------------------------------- */}

                    {/* -------------------------- [ FOOTER ] ------------------------------------ */}
                    <div className="px-6 sm:px-8 py-5 smallTablet:px-4 smallTablet:py-4 border-t border-edge-faint flex flex-col-reverse sm:flex-row items-stretch sm:items-center gap-3 rounded-b-2xl">
                        <button type="button" onClick={onClose} disabled={loading} className={secondaryButtonClass}>
                            Cancel
                        </button>
                        <button
                            type="button"
                            onClick={updateChase}
                            disabled={loading}
                            className={`${primaryButtonClass} sm:ml-auto sm:min-w-[180px]`}
                        >
                            {loading ? <FontAwesomeIcon icon={faCircleNotch} className="fa-spin text-[14px]" /> : "Update chase"}
                        </button>
                    </div>
                    {/* -------------------------- [ FOOTER ] ------------------------------------ */}
                </div>
            </div>
        </ModalPortal>
    )
}

export default EditChaseModel